import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FlashOnOutlinedIcon from "@mui/icons-material/FlashOnOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { useSnackbar } from "notistack";
import CustomConfirmation from "src/components/ModalPopover/Confirmation";
import {
  AUTOMATION_SCOPE_TYPES,
  createEmptyWorkflow,
  getActionLabel,
  getDynamicFieldsForTracker,
  getTriggerLabel,
  groupDynamicFields,
  saveWorkflowOnAutomationViaApi,
  TAG_MODE_OPTIONS,
  toDynamicToken,
} from "../automations/automationStorage";
import EmailContentEditor from "../automations/EmailContentEditor";
import {
  BORDER,
  EndMarker,
  ErrorText,
  FieldLabel,
  INK,
  inputSx,
  LINE,
  MUTED,
  OptionTile,
  Panel,
  PillGroup,
  PRIMARY,
  PRIMARY_RING,
  PRIMARY_SOFT,
  StepCard,
} from "./builderUi";
import TagChipPicker from "../TagChipPicker";
import {
  applyTriggerChange,
  AUDIENCE_MESSAGE_TRIGGER,
  coachReviewSupport,
  emptyTriggerConfig,
  individualMemberWorkflowPatch,
  insertFieldsFor,
  isBuilderTrigger,
  recipientsForAudience,
  schedulePointsOf,
  triggerConfigFromWorkflow,
  triggerPatch,
  TriggerFields,
  validateTriggerConfig,
} from "./triggerConfig";
import {
  MAX_ATTACHMENT_BYTES,
  MAX_ATTACHMENTS,
  uploadEmailAttachment,
} from "./attachmentApi";
import { appendWorkflowOnAutomationViaApi } from "./workflowApi";
// AI provider settings UI hidden — keys stay in server env / admin config.
// import AiProviderSettingsDrawer, {
//   aiProviderLabel,
//   loadAiProviderSetting,
// } from "./AiProviderSettingsDrawer";

const ACTION_ICONS = {
  email: MailOutlineIcon,
  record_event: HistoryOutlinedIcon,
  notify: NotificationsNoneOutlinedIcon,
  create_coach_review: RateReviewOutlinedIcon,
  assign_status: LabelOutlinedIcon,
  assign_tags: LocalOfferOutlinedIcon,
};

const INSERT_TARGETS = [
  { value: "subject", label: "Subject" },
  { value: "message", label: "Message" },
];

const WAIT_UNIT_OPTIONS = [
  { value: "minutes", label: "minutes" },
  { value: "hours", label: "hours" },
  { value: "days", label: "days" },
];

const MAX_CC = 10;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const YES_NO_OPTIONS = [
  { value: "no", label: "No" },
  { value: "yes", label: "Yes" },
];

/** Email token filled with the AI-written personal note for the report. */
const PROGRESS_REPORT_FIELD = {
  token: "progress_report_message",
  label: "AI Progress Message",
  group: "Standard",
};

/** Week keys for one report, ordered as on the journey schedule. */
const orderedWeekKeys = (weekKeys, schedulePoints) => {
  const wanted = new Set((weekKeys || []).map(String).filter(Boolean));
  if (!wanted.size) return [];
  const ordered = (schedulePoints || [])
    .map((p) => String(p.key))
    .filter((key) => wanted.has(key));
  // Keep any unknown keys at the end (legacy / renamed weeks).
  (weekKeys || []).forEach((key) => {
    const k = String(key || "");
    if (k && !ordered.includes(k)) ordered.push(k);
  });
  return ordered;
};

const emptyReportRange = () => ({
  id: `range_${Date.now()}`,
  sendOnPointKey: "",
  compareFromPointKeys: [],
});

/** Weeks that come before `sendOnKey` on the journey schedule. */
const weeksBeforeSendOn = (sendOnKey, schedulePoints = []) => {
  const idx = (schedulePoints || []).findIndex(
    (p) => String(p.key) === String(sendOnKey || "")
  );
  if (idx <= 0) return [];
  return schedulePoints.slice(0, idx);
};

/**
 * Normalize report ranges for the editor.
 * Preferred: sendOnPointKey + compareFromPointKeys.
 * Legacy: weekKeys (last = send, earlier = compare) or from/to.
 */
const reportRangesOf = (report, schedulePoints = []) => {
  if (!report) return [];
  const normalizeRow = (row, index) => {
    let sendOnPointKey = String(row?.sendOnPointKey || "").trim();
    let compareFromPointKeys = Array.isArray(row?.compareFromPointKeys)
      ? row.compareFromPointKeys.map(String).filter(Boolean)
      : [];
    const weekKeys = Array.isArray(row?.weekKeys)
      ? row.weekKeys.map(String).filter(Boolean)
      : [];

    if (!sendOnPointKey && weekKeys.length) {
      sendOnPointKey = weekKeys[weekKeys.length - 1];
      if (!compareFromPointKeys.length) {
        compareFromPointKeys = weekKeys.slice(0, -1);
      }
    }
    if (!sendOnPointKey && (row?.fromPointKey || row?.toPointKey)) {
      sendOnPointKey = String(row.toPointKey || row.fromPointKey || "");
      if (
        !compareFromPointKeys.length &&
        row.fromPointKey &&
        String(row.fromPointKey) !== sendOnPointKey
      ) {
        compareFromPointKeys = [String(row.fromPointKey)];
      }
    }

    const beforeKeys = new Set(
      weeksBeforeSendOn(sendOnPointKey, schedulePoints).map((p) => String(p.key))
    );
    compareFromPointKeys = orderedWeekKeys(
      compareFromPointKeys.filter(
        (key) => key !== sendOnPointKey && (!beforeKeys.size || beforeKeys.has(key))
      ),
      schedulePoints
    );

    return {
      id: row?.id || `range_${index}`,
      sendOnPointKey,
      compareFromPointKeys,
    };
  };

  if (Array.isArray(report.ranges) && report.ranges.length) {
    return report.ranges.map(normalizeRow);
  }
  if (
    report.sendOnPointKey ||
    report.fromPointKey ||
    report.toPointKey ||
    (Array.isArray(report.weekKeys) && report.weekKeys.length)
  ) {
    return [normalizeRow(report, 0)];
  }
  return [{ id: "range_0", sendOnPointKey: "", compareFromPointKeys: [] }];
};

/** "" when the step's progress report settings are fine, otherwise the error. */
const progressReportError = (report, schedulePoints) => {
  if (!report?.enabled) return "";
  if (!schedulePoints.length) {
    return "This journey has no schedule weeks yet, so report weeks can't be chosen.";
  }
  const ranges = reportRangesOf(report, schedulePoints);
  if (!ranges.length) return "Add at least one progress report";
  for (let i = 0; i < ranges.length; i += 1) {
    const label = ranges.length > 1 ? `report ${i + 1}` : "the report";
    if (!ranges[i].sendOnPointKey) {
      return `Pick which week to send ${label} on`;
    }
    if (!(ranges[i].compareFromPointKeys || []).length) {
      return `Pick at least one earlier week to compare ${label} from`;
    }
  }
  return "";
};

/** "" when the CC list is fine, otherwise the error to show. */
const ccError = (value) => {
  const list = String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
  if (list.length > MAX_CC) return `Up to ${MAX_CC} CC emails`;
  if (list.some((item) => !EMAIL_RE.test(item))) {
    return "Enter valid emails separated by commas";
  }
  return "";
};

const newStepId = () =>
  `step_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const createActionStep = (overrides = {}) => ({
  id: newStepId(),
  type: "action",
  action: "email",
  recipients: "person",
  cc: "",
  attachments: [],
  subject: "",
  message: "",
  assignStatusName: "",
  assignTags: [],
  tagMode: "add",
  ...overrides,
});

const createWaitStep = () => ({
  id: newStepId(),
  type: "wait",
  amount: 1,
  unit: "days",
});

/** Saved steps, or the legacy single action as step 1. */
const stepsFromWorkflow = (workflow) => {
  if (Array.isArray(workflow?.steps) && workflow.steps.length) {
    return workflow.steps.map((step) => ({
      ...step,
      id: step.id || newStepId(),
    }));
  }
  return [
    createActionStep({
      action: workflow?.action || "email",
      recipients: workflow?.recipients || "person",
      subject: workflow?.subject || "",
      message: workflow?.message || "",
      assignStatusName: workflow?.assignStatusName || "",
      assignTags: Array.isArray(workflow?.assignTags) ? workflow.assignTags : [],
      tagMode: workflow?.tagMode || "add",
      attachments: Array.isArray(workflow?.attachments)
        ? workflow.attachments
        : [],
      cc: workflow?.cc || "",
    }),
  ];
};

const stepTitle = (step) => {
  if (step.type === "wait") return "Wait";
  const action = step.action || "email";
  return action === "email" ? "Send email" : getActionLabel(action);
};

/** The step's own name, or its type ("Send email", "Wait"…). */
const stepName = (step) => String(step?.name || "").trim() || stepTitle(step);

const stepIconOf = (step) =>
  step.type === "wait"
    ? AccessTimeOutlinedIcon
    : ACTION_ICONS[step.action || "email"] || MailOutlineIcon;

/** One line under a step card: what it will do. */
const stepSummary = (step) => {
  if (step.type === "wait") {
    return `Wait ${step.amount || 1} ${step.unit || "days"}, then continue`;
  }
  const action = step.action || "email";
  if (action === "email" || action === "notify") {
    return String(step.subject || "").trim() || "No subject yet";
  }
  if (action === "assign_tags") {
    const tags = Array.isArray(step.assignTags) ? step.assignTags : [];
    const mode =
      TAG_MODE_OPTIONS.find((option) => option.value === (step.tagMode || "add"))
        ?.label || "Add tags";
    return tags.length ? `${mode}: ${tags.join(", ")}` : "No tags picked yet";
  }
  if (action === "assign_status") {
    return step.assignStatusName
      ? `Status: ${step.assignStatusName}`
      : "No status picked yet";
  }
  return "";
};

/** Side panel subtitle for each kind of step. */
const STEP_DESCRIPTIONS = {
  wait: "Pauses the workflow before the next step",
  email: "Sends an email with your subject and message",
  notify: "Sends a notification",
  assign_tags: "Adds, removes or replaces tags on the member",
  assign_status: "Sets the member's status on this journey",
};
const stepDescription = (step) =>
  STEP_DESCRIPTIONS[step.type === "wait" ? "wait" : step.action || "email"] || "";

const STEP_ERROR_KEYS = ["cc", "subject", "message", "report", "tags"];

const TokenChip = ({ label, token, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    title={toDynamicToken(token)}
    sx={{
      border: `1px solid ${BORDER}`,
      bgcolor: "#fff",
      borderRadius: "6px",
      px: 1,
      py: 0.35,
      fontFamily: "inherit",
      fontSize: 12,
      fontWeight: 500,
      color: "#475569",
      cursor: "pointer",
      lineHeight: 1.35,
      "&:hover": {
        borderColor: PRIMARY,
        bgcolor: PRIMARY_SOFT,
        color: PRIMARY,
      },
    }}
  >
    {label}
  </Box>
);

/** "+" between steps — adds a Send email or Wait step there. */
const AddStepButton = ({ onAdd, emailOnly = false }) => {
  const [anchor, setAnchor] = useState(null);
  const pick = (type) => {
    setAnchor(null);
    onAdd(type);
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: 0.25,
      }}
    >
      <Box sx={{ width: 2, height: 14, bgcolor: LINE }} />
      <Tooltip title="Add step">
        <IconButton
          aria-label="Add step"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            width: 30,
            height: 30,
            bgcolor: PRIMARY,
            color: "#fff",
            "&:hover": { bgcolor: PRIMARY, opacity: 0.9 },
          }}
        >
          <AddRoundedIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>
      <Box sx={{ width: 2, height: 14, bgcolor: LINE }} />
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
      >
        <MenuItem onClick={() => pick("email")} sx={{ gap: 1, fontSize: 14 }}>
          <MailOutlineIcon sx={{ fontSize: 18, color: MUTED }} />
          Send email
        </MenuItem>
        {!emailOnly ? (
          <MenuItem
            onClick={() => pick("assign_tags")}
            sx={{ gap: 1, fontSize: 14 }}
          >
            <LocalOfferOutlinedIcon sx={{ fontSize: 18, color: MUTED }} />
            Assign tags
          </MenuItem>
        ) : null}
        {!emailOnly ? (
          <MenuItem
            onClick={() => pick("assign_status")}
            sx={{ gap: 1, fontSize: 14 }}
          >
            <LabelOutlinedIcon sx={{ fontSize: 18, color: MUTED }} />
            Assign status
          </MenuItem>
        ) : null}
        <MenuItem onClick={() => pick("wait")} sx={{ gap: 1, fontSize: 14 }}>
          <AccessTimeOutlinedIcon sx={{ fontSize: 18, color: MUTED }} />
          Wait
        </MenuItem>
      </Menu>
    </Box>
  );
};

/**
 * Card for one step in the flow: icon, name, "Step N" and a one-line summary. A click opens the
 * step's settings in the side panel; move up / down and delete stay on the card.
 */
const StepShell = ({
  icon,
  title,
  summary,
  index,
  isFirst,
  isLast,
  selected,
  hasError,
  onOpen,
  onMove,
  onDelete,
}) => (
  <Panel
    sx={{
      overflow: "hidden",
      borderColor: selected ? PRIMARY : hasError ? "#fca5a5" : BORDER,
      boxShadow: selected ? `0 0 0 3px ${PRIMARY_RING}` : "none",
      transition: "border-color .15s ease, box-shadow .15s ease",
      "&:hover": { borderColor: selected ? PRIMARY : LINE },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1.5 }}>
      <Box
        component="button"
        type="button"
        onClick={onOpen}
        aria-label={`Open ${title} (step ${index + 1})`}
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 2,
          py: 1.25,
          border: 0,
          background: "none",
          font: "inherit",
          textAlign: "left",
          cursor: "pointer",
          "&:focus-visible": { outline: `2px solid ${PRIMARY}`, outlineOffset: -2 },
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "8px",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            bgcolor: PRIMARY_SOFT,
            color: PRIMARY,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, minWidth: 0 }}>
            <Typography noWrap sx={{ fontWeight: 700, fontSize: 15, color: INK }}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: 12, color: MUTED, flexShrink: 0 }}>
              Step {index + 1}
            </Typography>
          </Box>
          {summary ? (
            <Typography noWrap sx={{ fontSize: 13, color: MUTED, mt: 0.25 }}>
              {summary}
            </Typography>
          ) : null}
          {hasError ? (
            <Typography sx={{ fontSize: 12, color: "#dc2626", mt: 0.25 }}>
              Needs attention — open to fix
            </Typography>
          ) : null}
        </Box>
      </Box>
      <Box sx={{ display: "flex", gap: 0.25, flexShrink: 0 }}>
        <Tooltip title="Move up">
          <span>
            <IconButton
              size="small"
              aria-label="Move up"
              disabled={isFirst}
              onClick={() => onMove(-1)}
              sx={{ color: MUTED }}
            >
              <ArrowUpwardRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Move down">
          <span>
            <IconButton
              size="small"
              aria-label="Move down"
              disabled={isLast}
              onClick={() => onMove(1)}
              sx={{ color: MUTED }}
            >
              <ArrowDownwardRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Edit">
          <IconButton
            size="small"
            aria-label="Edit step"
            onClick={onOpen}
            sx={{ color: MUTED, "&:hover": { color: PRIMARY } }}
          >
            <EditOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton
            size="small"
            aria-label="Delete step"
            onClick={onDelete}
            sx={{ color: MUTED, "&:hover": { color: "#dc2626" } }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  </Panel>
);

/** Uploaded files on an email step + the "Attach a file" button. */
const AttachmentsField = ({ attachments, onChange }) => {
  const { enqueueSnackbar } = useSnackbar();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const files = Array.isArray(attachments) ? attachments : [];

  const handleFiles = async (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = "";
    if (!picked.length) return;
    const room = MAX_ATTACHMENTS - files.length;
    if (picked.length > room) {
      enqueueSnackbar(`Up to ${MAX_ATTACHMENTS} files`, { variant: "warning" });
    }
    const allowed = picked.slice(0, Math.max(0, room));
    const tooBig = allowed.filter((file) => file.size > MAX_ATTACHMENT_BYTES);
    if (tooBig.length) {
      enqueueSnackbar("Each file can be up to 10 MB", { variant: "warning" });
    }
    const toUpload = allowed.filter(
      (file) => file.size <= MAX_ATTACHMENT_BYTES
    );
    if (!toUpload.length) return;

    setUploading(true);
    const added = [];
    for (const file of toUpload) {
      try {
        added.push(await uploadEmailAttachment(file));
      } catch (err) {
        enqueueSnackbar(err?.message || `Could not upload ${file.name}`, {
          variant: "error",
        });
      }
    }
    setUploading(false);
    if (added.length) onChange([...files, ...added]);
  };

  return (
    <>
      <FieldLabel sx={{ mt: 2 }}>Attachments</FieldLabel>
      {files.length ? (
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 0.75, mb: 1 }}
        >
          {files.map((file, i) => (
            <Box
              key={`${file.path || file.url}-${i}`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.25,
                py: 0.6,
                border: `1px solid ${BORDER}`,
                borderRadius: "8px",
                bgcolor: "#fff",
              }}
            >
              <InsertDriveFileOutlinedIcon
                sx={{ fontSize: 18, color: MUTED }}
              />
              <Typography
                component="a"
                href={file.url}
                target="_blank"
                rel="noreferrer"
                sx={{
                  fontSize: 13,
                  color: INK,
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {file.name || "Attachment"}
              </Typography>
              <IconButton
                size="small"
                aria-label={`Remove ${file.name || "attachment"}`}
                onClick={() => onChange(files.filter((_, j) => j !== i))}
                sx={{ color: MUTED }}
              >
                <CloseRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      ) : null}
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        onChange={handleFiles}
        data-testid="attachment-input"
      />
      <button
        type="button"
        className="small-contained-outlined-button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading || files.length >= MAX_ATTACHMENTS}
        style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
      >
        <AttachFileRoundedIcon sx={{ fontSize: 17 }} />
        {uploading ? "Uploading…" : "Attach a file"}
      </button>
    </>
  );
};

/**
 * Progress report on an email step: pick which week to send each PDF on,
 * and which earlier weeks to compare from. Built on check-in — never on save.
 * `schedulePoints` = weeks on this workflow (or all if Every schedule).
 */
const ProgressReportFields = ({
  step,
  idPrefix,
  schedulePoints,
  errorText,
  onChange,
}) => {
  const report = step.progressReport || {};
  const enabled = Boolean(report.enabled);
  const ranges = reportRangesOf(report, schedulePoints);
  const setReport = (patch) =>
    onChange({ progressReport: { ...report, ...patch } });
  const setRanges = (nextRanges) =>
    setReport({
      ranges: nextRanges,
      fromPointKey: undefined,
      toPointKey: undefined,
      fromPoint: undefined,
      toPoint: undefined,
      sendOnPointKey: undefined,
      compareFromPointKeys: undefined,
      weekKeys: undefined,
    });

  const setSendOn = (rangeIndex, key) => {
    setRanges(
      ranges.map((row, i) => {
        if (i !== rangeIndex) return row;
        const sendOnPointKey = row.sendOnPointKey === key ? "" : key;
        const before = new Set(
          weeksBeforeSendOn(sendOnPointKey, schedulePoints).map((p) =>
            String(p.key)
          )
        );
        return {
          ...row,
          sendOnPointKey,
          compareFromPointKeys: orderedWeekKeys(
            (row.compareFromPointKeys || []).filter((k) => before.has(k)),
            schedulePoints
          ),
        };
      })
    );
  };

  const toggleCompareFrom = (rangeIndex, key) => {
    setRanges(
      ranges.map((row, i) => {
        if (i !== rangeIndex) return row;
        const set = new Set(row.compareFromPointKeys || []);
        if (set.has(key)) set.delete(key);
        else set.add(key);
        return {
          ...row,
          compareFromPointKeys: orderedWeekKeys(
            Array.from(set),
            schedulePoints
          ),
        };
      })
    );
  };

  const weekChipSx = (selected) => ({
    fontWeight: selected ? 700 : 500,
    bgcolor: selected
      ? "color-mix(in srgb, var(--portal-theme-primary) 16%, white)"
      : "#f1f5f9",
    color: selected ? PRIMARY : MUTED,
    border: selected ? `1px solid ${PRIMARY}` : `1px solid ${BORDER}`,
    "&:hover": {
      bgcolor: selected
        ? "color-mix(in srgb, var(--portal-theme-primary) 22%, white)"
        : "#e2e8f0",
    },
  });

  return (
    <Box
      sx={{
        mt: 2,
        p: 1.5,
        borderRadius: "10px",
        bgcolor: "#f8fafc",
        border: `1px solid ${BORDER}`,
      }}
    >
      <FieldLabel>Progress report</FieldLabel>
      <Typography sx={{ fontSize: 12.5, color: MUTED, mb: 1 }}>
        Attach a progress report PDF with this email when the member checks in?
      </Typography>
      <PillGroup
        size="small"
        ariaLabel="Progress report"
        options={YES_NO_OPTIONS}
        value={enabled ? "yes" : "no"}
        onChange={(value) =>
          setReport({
            enabled: value === "yes",
            ranges:
              value === "yes" && !ranges.length
                ? [emptyReportRange()]
                : ranges,
          })
        }
      />

      {enabled ? (
        <>
          {schedulePoints.length ? (
            <Box
              sx={{ mt: 1.75, display: "flex", flexDirection: "column", gap: 2 }}
            >
              {ranges.map((row, index) => {
                const compareOptions = weeksBeforeSendOn(
                  row.sendOnPointKey,
                  schedulePoints
                );
                return (
                  <Box
                    key={row.id || index}
                    sx={{
                      p: 1.5,
                      borderRadius: "10px",
                      border: `1px solid ${BORDER}`,
                      bgcolor: "#fff",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Typography
                        sx={{ fontSize: 13, fontWeight: 700, color: INK }}
                      >
                        {ranges.length > 1
                          ? `Report ${index + 1}`
                          : "Report timing"}
                      </Typography>
                      {ranges.length > 1 ? (
                        <button
                          type="button"
                          className="small-contained-outlined-button"
                          onClick={() =>
                            setRanges(ranges.filter((_, i) => i !== index))
                          }
                          style={{ padding: "6px 10px", whiteSpace: "nowrap" }}
                        >
                          Remove
                        </button>
                      ) : null}
                    </Box>

                    <Typography
                      sx={{ fontSize: 12.5, fontWeight: 600, color: INK, mb: 0.5 }}
                    >
                      Send report on
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: MUTED, mb: 1 }}>
                      Which selected week should trigger this report when the
                      member checks in?
                    </Typography>
                    <Box
                      id={`${idPrefix}-report-send-${index}`}
                      sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.75 }}
                    >
                      {schedulePoints.map((point) => {
                        const selected = row.sendOnPointKey === point.key;
                        return (
                          <Chip
                            key={point.key}
                            label={point.label || point.key}
                            size="small"
                            onClick={() => setSendOn(index, point.key)}
                            sx={weekChipSx(selected)}
                          />
                        );
                      })}
                    </Box>

                    <Typography
                      sx={{ fontSize: 12.5, fontWeight: 600, color: INK, mb: 0.5 }}
                    >
                      Compare from
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: MUTED, mb: 1 }}>
                      {row.sendOnPointKey
                        ? "Pick earlier week(s) to compare against. The PDF covers from the earliest compare week through the send week."
                        : "Pick a send week first, then choose earlier weeks to compare from."}
                    </Typography>
                    {row.sendOnPointKey && compareOptions.length ? (
                      <Box
                        id={`${idPrefix}-report-compare-${index}`}
                        sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}
                      >
                        {compareOptions.map((point) => {
                          const selected = (
                            row.compareFromPointKeys || []
                          ).includes(point.key);
                          return (
                            <Chip
                              key={point.key}
                              label={point.label || point.key}
                              size="small"
                              onClick={() =>
                                toggleCompareFrom(index, point.key)
                              }
                              sx={weekChipSx(selected)}
                            />
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: 12, color: MUTED }}>
                        {row.sendOnPointKey
                          ? "No earlier weeks available before the send week."
                          : null}
                      </Typography>
                    )}
                  </Box>
                );
              })}
              <Box>
                <button
                  type="button"
                  className="small-contained-outlined-button"
                  onClick={() => setRanges([...ranges, emptyReportRange()])}
                  style={{ padding: "8px 12px" }}
                >
                  Add another report
                </button>
              </Box>
            </Box>
          ) : (
            <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 1.5 }}>
              No schedule weeks on this workflow yet. Pick weeks under the
              trigger (or leave Every schedule) so report weeks can be chosen.
            </Typography>
          )}
          {errorText ? <ErrorText>{errorText}</ErrorText> : null}

          <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 1.5 }}>
            Each report is only built when the check-in falls in its send week
            (wins only). Add <b>AI Progress Message</b> in the body for the
            personal note.
          </Typography>
        </>
      ) : null}
    </Box>
  );
};

const PREVIEW_SAMPLES = {
  first_name: "Alex",
  last_name: "Murphy",
  member_name: "Alex Murphy",
  email: "alex@example.com",
  journey_name: "Online Coaching",
  coach_name: "Seamus",
  check_in_day: "Thursday",
  follow_up_date: "12 Nov 2026",
  review_date: "13 Nov 2026",
};

const fillPreviewTokens = (template, dynamicFields = []) => {
  const byToken = Object.fromEntries(
    (dynamicFields || []).map((f) => [String(f.token || "").toLowerCase(), f])
  );
  return String(template || "").replace(
    /\{\{\s*([a-z0-9_]+)\s*\}\}/gi,
    (_, key) => {
      const k = String(key).toLowerCase();
      if (PREVIEW_SAMPLES[k] != null) return PREVIEW_SAMPLES[k];
      const field = byToken[k];
      return field?.label ? `[${field.label}]` : `{{${key}}}`;
    }
  );
};

/** CC, Subject, Body, Insert field and Attachments for one email step. */
const MessageStepFields = ({
  step,
  idPrefix,
  dynamicFields,
  ccErrorText,
  subjectErrorText,
  messageErrorText,
  onChange,
}) => {
  const [insertTarget, setInsertTarget] = useState("message");
  const [tokenSearch, setTokenSearch] = useState("");
  const [ccOpen, setCcOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const subjectRef = useRef(null);
  const messageRef = useRef(null);
  const editorRef = useRef(null);

  const isEmail = (step.action || "email") === "email";
  const previewSubject = fillPreviewTokens(
    step.subject || "Update on your journey",
    dynamicFields
  );
  const previewBody = fillPreviewTokens(
    step.message || "You have an update on {{journey_name}}.",
    dynamicFields
  );

  const groupEntries = useMemo(() => {
    const q = tokenSearch.trim().toLowerCase();
    const source = q
      ? dynamicFields.filter(
          (field) =>
            field.label?.toLowerCase().includes(q) ||
            field.token?.toLowerCase().includes(q)
        )
      : dynamicFields;
    return Object.entries(groupDynamicFields(source));
  }, [dynamicFields, tokenSearch]);

  const focusTarget = (target) => {
    setInsertTarget(target);
    if (target === "subject") subjectRef.current?.focus();
    else if (isEmail) editorRef.current?.focus();
    else messageRef.current?.focus();
  };

  const insertToken = (token) => {
    const marker = toDynamicToken(token);
    if (insertTarget === "message" && isEmail && editorRef.current) {
      editorRef.current.insertContent(marker);
      return;
    }
    const field = insertTarget === "subject" ? "subject" : "message";
    const el =
      insertTarget === "subject" ? subjectRef.current : messageRef.current;
    const current = step[field] || "";
    if (el && typeof el.selectionStart === "number") {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      onChange({
        [field]: current.slice(0, start) + marker + current.slice(end),
      });
      requestAnimationFrame(() => {
        el.focus();
        const pos = start + marker.length;
        el.setSelectionRange(pos, pos);
      });
    } else {
      onChange({ [field]: `${current}${marker}` });
    }
  };

  return (
    <>
      {isEmail && !(ccOpen || step.cc || ccErrorText) ? (
        <button
          type="button"
          className="small-contained-outlined-button"
          onClick={() => setCcOpen(true)}
          style={{ fontSize: 12, padding: "4px 12px" }}
        >
          Cc
        </button>
      ) : null}
      {isEmail && (ccOpen || step.cc || ccErrorText) ? (
        <>
          <FieldLabel htmlFor={`${idPrefix}-cc`}>CC (optional)</FieldLabel>
          <TextField
            id={`${idPrefix}-cc`}
            fullWidth
            size="small"
            placeholder="e.g. office@example.com, manager@example.com"
            value={step.cc || ""}
            onChange={(e) => onChange({ cc: e.target.value })}
            autoFocus={ccOpen && !step.cc}
            error={Boolean(ccErrorText)}
            sx={inputSx}
          />
          {ccErrorText ? <ErrorText>{ccErrorText}</ErrorText> : null}
        </>
      ) : null}

      <FieldLabel htmlFor={`${idPrefix}-subject`} required={isEmail} sx={{ mt: isEmail ? 2 : 0 }}>
        Subject
      </FieldLabel>
      <TextField
        id={`${idPrefix}-subject`}
        fullWidth
        size="small"
        value={step.subject || ""}
        onChange={(e) => onChange({ subject: e.target.value })}
        inputRef={subjectRef}
        onFocus={() => setInsertTarget("subject")}
        error={Boolean(subjectErrorText)}
        sx={inputSx}
      />
      {subjectErrorText ? <ErrorText>{subjectErrorText}</ErrorText> : null}

      <FieldLabel htmlFor={`${idPrefix}-message`} sx={{ mt: 2 }}>
        Body
      </FieldLabel>
      {isEmail ? (
        <EmailContentEditor
          value={step.message || ""}
          onChange={(html) => onChange({ message: html, bodyFormat: "html" })}
          onEditorReady={(editor) => {
            editorRef.current = editor;
          }}
          onFocus={() => setInsertTarget("message")}
          height={280}
        />
      ) : (
        <TextField
          id={`${idPrefix}-message`}
          fullWidth
          multiline
          minRows={6}
          value={step.message || ""}
          onChange={(e) => onChange({ message: e.target.value })}
          inputRef={messageRef}
          onFocus={() => setInsertTarget("message")}
          error={Boolean(messageErrorText)}
          sx={inputSx}
        />
      )}
      {messageErrorText ? <ErrorText>{messageErrorText}</ErrorText> : null}

      {isEmail ? (
        <Box sx={{ mt: 1.5, display: "flex", justifyContent: "flex-end" }}>
          <button
            type="button"
            className="small-contained-outlined-button"
            onClick={() => setPreviewOpen(true)}
          >
            Preview email
          </button>
          <Dialog
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
              Email preview
              <IconButton
                onClick={() => setPreviewOpen(false)}
                sx={{ position: "absolute", right: 8, top: 8 }}
                aria-label="Close preview"
              >
                <CloseRoundedIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Typography sx={{ fontSize: 12.5, color: "#64748b", mb: 1.5 }}>
                Sample data only — real emails use each member&apos;s details.
                An unsubscribe link is added automatically when the email is
                sent.
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#64748b", mb: 0.5 }}>
                Subject
              </Typography>
              <Typography sx={{ fontWeight: 700, mb: 2 }}>{previewSubject}</Typography>
              <Typography sx={{ fontSize: 12, color: "#64748b", mb: 0.5 }}>
                Body
              </Typography>
              <Box
                sx={{
                  border: `1px solid ${BORDER}`,
                  borderRadius: "10px",
                  p: 2,
                  bgcolor: "#fff",
                  "& p": { m: "0 0 0.75em" },
                }}
                dangerouslySetInnerHTML={{
                  __html:
                    step.bodyFormat === "html" || /<\/?[a-z][\s\S]*>/i.test(previewBody)
                      ? previewBody
                      : `<p>${String(previewBody)
                          .replace(/&/g, "&amp;")
                          .replace(/</g, "&lt;")
                          .replace(/>/g, "&gt;")
                          .replace(/\n/g, "<br/>")}</p>`,
                }}
              />
              <Typography sx={{ fontSize: 12, color: "#64748b", mt: 2 }}>
                Unsubscribe from these emails (link added on send)
              </Typography>
            </DialogContent>
          </Dialog>
        </Box>
      ) : null}

      <Box
        sx={{
          mt: 1.75,
          p: 1.5,
          borderRadius: "10px",
          bgcolor: "#f8fafc",
          border: `1px solid ${BORDER}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
            mb: 1,
          }}
        >
          <FieldLabel sx={{ mb: 0 }}>Insert field</FieldLabel>
          <PillGroup
            size="small"
            ariaLabel="Insert field into"
            options={INSERT_TARGETS}
            value={insertTarget}
            onChange={focusTarget}
          />
        </Box>

        <TextField
          size="small"
          fullWidth
          placeholder="Search fields…"
          value={tokenSearch}
          onChange={(e) => setTokenSearch(e.target.value)}
          sx={{ ...inputSx, mb: 1.25 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: MUTED }} />
              </InputAdornment>
            ),
          }}
        />

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.65,
            maxHeight: 160,
            overflowY: "auto",
          }}
        >
          {groupEntries.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: MUTED }}>
              No fields match.
            </Typography>
          ) : (
            groupEntries.flatMap(([, fields]) =>
              fields.map((field) => (
                <TokenChip
                  key={field.token}
                  label={field.label}
                  token={field.token}
                  onClick={() => insertToken(field.token)}
                />
              ))
            )
          )}
        </Box>
      </Box>

      {isEmail ? (
        <AttachmentsField
          attachments={step.attachments}
          onChange={(attachments) => onChange({ attachments })}
        />
      ) : null}
    </>
  );
};

/**
 * Flow-style workflow editor: Trigger → steps (Send email / Wait) → End.
 * The trigger is For coach / member → review, reminder or New Submission;
 * reminders also take before / after the due date + days. Steps are saved on the workflow, with the first
 * email mirrored onto the workflow's own action / subject / message.
 */
export default function WorkflowBuilderPanel({
  automation,
  tracker,
  workflowId,
  isNew,
  initialName = "",
  onSaved,
  onCancel,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState(createEmptyWorkflow);
  const [trigger, setTrigger] = useState(emptyTriggerConfig);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Load the draft when the selected workflow changes — not on every parent
  // save, so unsaved edits here survive an automation name/enabled save.
  const automationRef = useRef(automation);
  automationRef.current = automation;
  const automationId = automation?.id;

  useEffect(() => {
    const current = automationRef.current;
    if (!current) return;
    if (isNew) {
      const base = createEmptyWorkflow();
      setForm({
        ...base,
        name: initialName,
        action: "email",
        steps: [
          createActionStep({ subject: base.subject, message: base.message }),
        ],
      });
      setTrigger(emptyTriggerConfig());
    } else {
      const existing = (current.workflows || []).find(
        (item) => item.id === workflowId
      );
      setForm(
        existing
          ? {
              ...createEmptyWorkflow(),
              ...existing,
              steps: stepsFromWorkflow(existing),
            }
          : null
      );
      setTrigger(
        existing ? triggerConfigFromWorkflow(existing) : emptyTriggerConfig()
      );
    }
    setErrors({});
    // initialName only matters for a new draft; the parent re-keys the
    // panel for each one.
  }, [automationId, workflowId, isNew]);

  // Individual member = general-notification style (no journey triggers).
  const isIndividual =
    automation?.scopeType === AUTOMATION_SCOPE_TYPES.INDIVIDUAL;

  // Insert-field chips that have a value for the chosen trigger.
  const trackerFields = useMemo(
    () => getDynamicFieldsForTracker(tracker),
    [tracker]
  );
  const dynamicFields = useMemo(
    () =>
      insertFieldsFor(
        isIndividual ? { triggerId: AUDIENCE_MESSAGE_TRIGGER } : trigger,
        isIndividual ? [] : trackerFields
      ),
    [isIndividual, trigger, trackerFields]
  );

  const savedWorkflow = useMemo(
    () =>
      (automation?.workflows || []).find((item) => item.id === workflowId) ||
      null,
    [automation, workflowId]
  );

  // Journey schedule points of the automation's tracker (Week 1, Week 2…).
  const schedulePoints = useMemo(
    () =>
      isIndividual ? [] : schedulePointsOf(tracker, savedWorkflow),
    [isIndividual, tracker, savedWorkflow]
  );

  // Coach reviews this tracker can actually send (none on lead trackers).
  const reviewSupport = useMemo(() => coachReviewSupport(tracker), [tracker]);

  const trackerStatusNames = useMemo(() => {
    const names = (Array.isArray(tracker?.statuses) ? tracker.statuses : [])
      .map((s) => String(s?.name || s?.label || "").trim())
      .filter(Boolean);
    return Array.from(new Set(names));
  }, [tracker]);

  const trackerTagOptions = useMemo(() => {
    const names = (Array.isArray(tracker?.tags) ? tracker.tags : [])
      .map((t) => String(t || "").trim())
      .filter(Boolean);
    return Array.from(new Set(names));
  }, [tracker]);

  const changeTrigger = (patch) => {
    setTrigger((prev) => applyTriggerChange(prev, patch));
    setErrors((prev) => ({
      ...prev,
      audience: "",
      triggerId: "",
      dueTiming: "",
      dueDays: "",
      schedulePointKeys: "",
    }));
  };

  const setSteps = (change) => {
    setForm((prev) =>
      prev ? { ...prev, steps: change(prev.steps || []) } : prev
    );
    setErrors((prev) => ({ ...prev, steps: "" }));
  };

  // Step errors are keyed by position, so drop them when steps shift.
  const clearStepErrors = () =>
    setErrors((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(
          ([key]) => !/^(cc|subject|message|report)-\d+$/.test(key)
        )
      )
    );

  // A new step opens in the side panel; Cancel there removes it again.
  const addStep = (index, type) => {
    let step;
    if (type === "wait") step = createWaitStep();
    else if (type === "assign_tags") {
      step = createActionStep({ action: "assign_tags", tagMode: "add" });
    } else if (type === "assign_status") {
      step = createActionStep({ action: "assign_status" });
    } else {
      step = createActionStep({ action: "email" });
    }
    setSteps((steps) => [...steps.slice(0, index), step, ...steps.slice(index)]);
    clearStepErrors();
    openStep(step, true);
  };

  const updateStep = (index, patch) => {
    setSteps((steps) =>
      steps.map((step, i) => (i === index ? { ...step, ...patch } : step))
    );
    const edited = ["cc", "subject", "message"].filter((key) => key in patch);
    if ("progressReport" in patch) edited.push("report");
    if (edited.length) {
      setErrors((prev) => {
        const next = { ...prev };
        edited.forEach((key) => {
          next[`${key}-${index}`] = "";
        });
        return next;
      });
    }
  };

  const moveStep = (index, delta) => {
    setSteps((steps) => {
      const target = index + delta;
      if (target < 0 || target >= steps.length) return steps;
      const next = [...steps];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    clearStepErrors();
  };

  const removeStep = (index) => {
    setSteps((steps) => steps.filter((_, i) => i !== index));
    clearStepErrors();
  };

  // Step waiting for "Delete this step?" to be confirmed.
  const [stepToDelete, setStepToDelete] = useState(null);

  // Step open in the side panel: its id, a copy for Cancel, and whether it was just added.
  const [panel, setPanel] = useState(null);
  function openStep(step, isNewStep = false) {
    setPanel({ id: step.id, snapshot: JSON.parse(JSON.stringify(step)), isNew: isNewStep });
  }
  // Closing (X, outside click, Esc) keeps the edits; Cancel undoes them.
  const closePanel = () => setPanel(null);
  const cancelPanel = () => {
    if (!panel) return;
    const index = (form?.steps || []).findIndex((step) => step.id === panel.id);
    if (panel.isNew) {
      setSteps((steps) => steps.filter((step) => step.id !== panel.id));
      clearStepErrors();
    } else if (index >= 0) {
      setSteps((steps) =>
        steps.map((step) => (step.id === panel.id ? panel.snapshot : step))
      );
      setErrors((prev) => {
        const next = { ...prev };
        STEP_ERROR_KEYS.forEach((key) => delete next[`${key}-${index}`]);
        return next;
      });
    }
    setPanel(null);
  };
  // The open step was deleted → close the panel.
  useEffect(() => {
    if (panel && !(form?.steps || []).some((step) => step.id === panel.id)) {
      setPanel(null);
    }
  }, [form, panel]);
  const stepHasError = (index) =>
    STEP_ERROR_KEYS.some((key) => errors[`${key}-${index}`]);

  // The name is edited from the workflow list, so the saved one wins.
  const workflowName = String(
    (isNew ? form?.name : savedWorkflow?.name ?? form?.name) || ""
  ).trim();

  // Older workflows keep their trigger until a new one is picked here.
  const keepsLegacyTrigger =
    !isIndividual &&
    !isNew &&
    !trigger.audience &&
    !isBuilderTrigger(form);

  // Report week pickers: workflow schedule selection, or all journey weeks.
  const reportSchedulePoints = (() => {
    const keys = Array.isArray(trigger?.schedulePointKeys)
      ? trigger.schedulePointKeys.map(String).filter(Boolean)
      : [];
    if (!keys.length) return schedulePoints;
    return schedulePoints.filter((point) => keys.includes(String(point.key)));
  })();

  // Progress report only for For member + New Submission (not coach / other triggers).
  const allowsProgressReport =
    !isIndividual &&
    trigger?.audience === "member" &&
    trigger?.triggerId === "new_submission";

  // Errors of one step, keyed like "subject-2" (none for wait steps).
  const stepErrorsOf = (step, index) => {
    const next = {};
    if (step.type !== "action") return next;
    const action = step.action || "email";
    if (isIndividual && action !== "email") return next;
    if (action === "assign_tags") {
      const tags = Array.isArray(step.assignTags) ? step.assignTags : [];
      if ((step.tagMode || "add") !== "set" && !tags.length) {
        next[`tags-${index}`] = "Pick at least one tag";
      }
      return next;
    }
    if (action !== "email" && action !== "notify") return next;
    const message = ccError(step.cc);
    if (message) next[`cc-${index}`] = message;
    if (action !== "email") return next;
    if (!String(step.subject || "").trim()) {
      next[`subject-${index}`] = "Subject is required";
    }
    if (!String(step.message || "").trim()) {
      next[`message-${index}`] = "Body is required";
    }
    if (isIndividual || !allowsProgressReport) return next;
    const reportError = progressReportError(
      step.progressReport,
      reportSchedulePoints
    );
    if (reportError) next[`report-${index}`] = reportError;
    return next;
  };

  const validate = () => {
    const next =
      isIndividual || keepsLegacyTrigger
        ? {}
        : validateTriggerConfig(trigger, reviewSupport, schedulePoints);
    if (!workflowName) next.name = "Name is required";
    if (
      !(form?.steps || []).some(
        (step) =>
          step.type === "action" &&
          (!isIndividual || (step.action || "email") === "email")
      )
    ) {
      next.steps = isIndividual
        ? "Add at least one email step"
        : "Add at least one action step";
    }
    (form?.steps || []).forEach((step, index) => {
      Object.assign(next, stepErrorsOf(step, index));
    });
    setErrors(next);
    if (next.name) enqueueSnackbar(next.name, { variant: "warning" });
    // A step with a problem opens in the side panel, where its fields are.
    const firstBad = (form?.steps || []).findIndex((_, index) =>
      STEP_ERROR_KEYS.some((key) => next[`${key}-${index}`])
    );
    if (firstBad >= 0) openStep(form.steps[firstBad]);
    return Object.keys(next).length === 0;
  };

  // The side panel's "Save action": check this step, then close.
  const savePanel = () => {
    const index = (form?.steps || []).findIndex((step) => step.id === panel?.id);
    if (index < 0) {
      setPanel(null);
      return;
    }
    const found = stepErrorsOf(form.steps[index], index);
    if (Object.keys(found).length) {
      setErrors((prev) => ({ ...prev, ...found }));
      return;
    }
    setPanel(null);
  };

  // Report settings saved on an email step, with copies of the chosen weeks
  // (in case the journey's weeks change later). Untouched steps keep none.
  const progressReportPayload = (step) => {
    if (!allowsProgressReport) return { enabled: false };
    const report = step.progressReport;
    if ((step.action || "email") !== "email" || !report) return undefined;
    if (!report.enabled) return { enabled: false };
    const pointFor = (key) =>
      schedulePoints.find((point) => point.key === key) || null;
    const ranges = reportRangesOf(report, reportSchedulePoints)
      .map((row) => {
        const sendOnPointKey = String(row.sendOnPointKey || "").trim();
        if (!sendOnPointKey) return null;
        const compareFromPointKeys = orderedWeekKeys(
          row.compareFromPointKeys,
          schedulePoints
        );
        const fromPointKey = compareFromPointKeys[0] || sendOnPointKey;
        const toPointKey = sendOnPointKey;
        const weekKeys = orderedWeekKeys(
          [...compareFromPointKeys, sendOnPointKey],
          schedulePoints
        );
        return {
          id: row.id,
          sendOnPointKey,
          compareFromPointKeys,
          weekKeys,
          fromPointKey,
          toPointKey,
          fromPoint: pointFor(fromPointKey),
          toPoint: pointFor(toPointKey),
        };
      })
      .filter(Boolean);
    const first = ranges[0] || null;
    return {
      enabled: true,
      ranges,
      sendOnPointKey: first?.sendOnPointKey || "",
      compareFromPointKeys: first?.compareFromPointKeys || [],
      fromPointKey: first?.fromPointKey || "",
      toPointKey: first?.toPointKey || "",
      fromPoint: first?.fromPoint || null,
      toPoint: first?.toPoint || null,
    };
  };

  const handleSave = async ({ asDraft = false } = {}) => {
    if (!automation?.id || !form) return;
    if (!validate()) return;
    const triggerFields = isIndividual
      ? individualMemberWorkflowPatch()
      : keepsLegacyTrigger
        ? {}
        : triggerPatch(trigger, schedulePoints);
    // Individual / member workflows email the main member; coach → coach.
    const stepRecipients = isIndividual
      ? "person"
      : keepsLegacyTrigger
        ? null
        : recipientsForAudience(trigger.audience);
    const steps = (form.steps || []).map((step) =>
      step.type === "wait"
        ? {
            id: step.id,
            type: "wait",
            ...(String(step.name || "").trim() ? { name: String(step.name).trim() } : {}),
            amount: Math.max(1, Math.floor(Number(step.amount) || 1)),
            unit: step.unit || "days",
          }
        : {
            ...step,
            action: isIndividual ? "email" : step.action || "email",
            subject: (step.subject || "").trim(),
            message: (step.message || "").trim(),
            bodyFormat:
              (isIndividual ? "email" : step.action || "email") === "email"
                ? step.bodyFormat || "html"
                : step.bodyFormat,
            cc: String(step.cc || "")
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean)
              .join(", "),
            attachments: Array.isArray(step.attachments)
              ? step.attachments
              : [],
            assignTags: isIndividual
              ? []
              : Array.isArray(step.assignTags)
                ? step.assignTags
                : [],
            tagMode: step.tagMode || "add",
            assignStatusName: isIndividual
              ? ""
              : step.assignStatusName || "",
            recipients: stepRecipients || step.recipients || "person",
            progressReport: isIndividual
              ? { enabled: false }
              : progressReportPayload(step),
          }
    );
    // The first action also lives on the workflow itself, so the list,
    // the old automation screen and single-action runs keep working.
    const firstAction = steps.find((step) => step.type === "action") || {};
    setSaving(true);
    let saved;
    // New workflows go to the end of the list.
    const saveWorkflow = isNew
      ? appendWorkflowOnAutomationViaApi
      : saveWorkflowOnAutomationViaApi;
    try {
      saved = await saveWorkflow(automation.id, {
        ...form,
        ...triggerFields,
        // No status switch in the bar: Save workflow publishes (Active), Save as draft keeps it a draft.
        status: asDraft ? "draft" : "active",
        name: workflowName,
        description: (form.description || "").trim(),
        steps,
        action: firstAction.action || "email",
        recipients: firstAction.recipients || "person",
        subject: firstAction.subject || "",
        message: firstAction.message || "",
        bodyFormat: firstAction.bodyFormat,
        attachments: firstAction.attachments || [],
        assignStatusName:
          firstAction.assignStatusName || form.assignStatusName || "",
        assignTags: firstAction.assignTags || form.assignTags || [],
        tagMode: firstAction.tagMode || form.tagMode || "add",
        requireAnyTag: Array.isArray(form.requireAnyTag)
          ? form.requireAnyTag
          : [],
      });
    } catch (err) {
      setSaving(false);
      enqueueSnackbar(err?.message || "Could not save workflow", {
        variant: "error",
      });
      return;
    }
    const workflow =
      (saved.workflows || []).find((item) => item.id === form.id) ||
      (saved.workflows || []).find((item) => item.name === workflowName) ||
      (saved.workflows || [])[0];
    enqueueSnackbar(isNew ? "Workflow created" : "Workflow saved", {
      variant: "success",
    });
    // Reset local state before the parent re-keys this panel.
    setSaving(false);
    onSaved?.(saved, workflow);
  };

  if (!form) {
    return (
      <Panel sx={{ p: 2.5 }}>
        <Typography sx={{ fontSize: 14, color: MUTED }}>
          Workflow not found.
        </Typography>
      </Panel>
    );
  }

  const steps = form.steps || [];
  const openIndex = panel ? steps.findIndex((step) => step.id === panel.id) : -1;
  const openStepData = openIndex >= 0 ? steps[openIndex] : null;
  const OpenStepIcon = openStepData ? stepIconOf(openStepData) : null;
  const legacyTriggerLabel =
    !isBuilderTrigger(form) && form.triggerId
      ? (form.triggerModel || "event") === "relative"
        ? getTriggerLabel("relative_offset")
        : (form.triggerModel || "event") === "calendar"
        ? getTriggerLabel("calendar_date")
        : getTriggerLabel(form.triggerId)
      : "";

  const renderStepBody = (step, index) => {
    if (step.type === "wait") {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            fontSize: 14,
            color: INK,
          }}
        >
          <Typography component="span" sx={{ fontWeight: 700, fontSize: 13 }}>
            Wait
          </Typography>
          <TextField
            size="small"
            type="number"
            inputProps={{ min: 1, "aria-label": "Wait amount" }}
            value={step.amount ?? 1}
            onChange={(e) =>
              updateStep(index, {
                amount:
                  e.target.value === ""
                    ? ""
                    : Math.max(1, Math.floor(Number(e.target.value)) || 1),
              })
            }
            sx={{ ...inputSx, width: 96 }}
          />
          <TextField
            select
            size="small"
            value={step.unit || "days"}
            onChange={(e) => updateStep(index, { unit: e.target.value })}
            inputProps={{ "aria-label": "Wait unit" }}
            sx={{ ...inputSx, width: 130 }}
          >
            {WAIT_UNIT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Typography component="span" sx={{ fontSize: 13, color: MUTED }}>
            then continue
          </Typography>
        </Box>
      );
    }

    const action = step.action || "email";
    if (action === "email" || action === "notify") {
      const withReport =
        allowsProgressReport &&
        action === "email" &&
        step.progressReport?.enabled;
      return (
        <>
          <MessageStepFields
            step={step}
            idPrefix={`workflow-step-${index}`}
            dynamicFields={
              withReport
                ? [...dynamicFields, PROGRESS_REPORT_FIELD]
                : dynamicFields
            }
            ccErrorText={errors[`cc-${index}`]}
            subjectErrorText={errors[`subject-${index}`]}
            messageErrorText={errors[`message-${index}`]}
            onChange={(patch) => updateStep(index, patch)}
          />
          {action === "email" && allowsProgressReport ? (
            <ProgressReportFields
              step={step}
              idPrefix={`workflow-step-${index}`}
              schedulePoints={reportSchedulePoints}
              errorText={errors[`report-${index}`]}
              onChange={(patch) => updateStep(index, patch)}
            />
          ) : null}
        </>
      );
    }
    if (action === "assign_status") {
      const current = String(step.assignStatusName || "").trim();
      const names =
        current && !trackerStatusNames.includes(current)
          ? [...trackerStatusNames, current]
          : trackerStatusNames;
      return (
        <>
          <FieldLabel htmlFor={`workflow-step-${index}-status`}>
            Status to assign
          </FieldLabel>
          {trackerStatusNames.length ? (
            <PillGroup
              ariaLabel="Status to assign"
              options={names.map((name) => ({ value: name, label: name }))}
              value={step.assignStatusName || ""}
              onChange={(value) =>
                updateStep(index, { assignStatusName: value })
              }
            />
          ) : (
            <TextField
              id={`workflow-step-${index}-status`}
              fullWidth
              size="small"
              value={step.assignStatusName || ""}
              onChange={(e) =>
                updateStep(index, { assignStatusName: e.target.value })
              }
              placeholder="Must match a status on this tracker"
              sx={inputSx}
            />
          )}
        </>
      );
    }
    if (action === "assign_tags") {
      return (
        <>
          <FieldLabel>Tag mode</FieldLabel>
          <PillGroup
            ariaLabel="Tag mode"
            options={TAG_MODE_OPTIONS}
            value={step.tagMode || "add"}
            onChange={(value) => updateStep(index, { tagMode: value })}
          />
          <Box sx={{ mt: 2 }}>
            <TagChipPicker
              label="Tags"
              options={trackerTagOptions}
              value={step.assignTags || []}
              onChange={(assignTags) => updateStep(index, { assignTags })}
              emptyLabel="Add tags on the journey first (Tracker → Tags)."
            />
            {errors[`tags-${index}`] ? (
              <ErrorText>{errors[`tags-${index}`]}</ErrorText>
            ) : null}
          </Box>
        </>
      );
    }
    return null;
  };

  return (
    <Box>
      {/* Individual: no journey trigger — emails go to chosen main members. */}
      {isIndividual ? (
        <StepCard
          tone="warm"
          icon={<MailOutlineIcon sx={{ fontSize: 19 }} />}
          title="For main member"
        >
          <Typography sx={{ fontSize: 13, color: MUTED, lineHeight: 1.5 }}>
            Like general notifications: no coach/member trigger. Emails go to
            the main members you chose (all / by journey / specific).
          </Typography>
        </StepCard>
      ) : (
        <StepCard
          tone="warm"
          icon={<FlashOnOutlinedIcon sx={{ fontSize: 19 }} />}
          title="Trigger"
        >
          {legacyTriggerLabel && !trigger.audience ? (
            <Box sx={{ mb: 2, maxWidth: 360 }}>
              <OptionTile
                selected
                title={legacyTriggerLabel}
                onClick={() => {}}
              />
            </Box>
          ) : null}
          <TriggerFields
            value={trigger}
            onChange={changeTrigger}
            errors={errors}
            idPrefix="workflow-trigger"
            schedulePoints={schedulePoints}
            support={reviewSupport}
          />
        </StepCard>
      )}

      {/* Steps: Send email / Wait */}
      <AddStepButton
        emailOnly={isIndividual}
        onAdd={(type) => addStep(0, type)}
      />
      {steps.map((step, index) => {
        const Icon = stepIconOf(step);
        return (
          <React.Fragment key={step.id}>
            <StepShell
              icon={<Icon sx={{ fontSize: 19 }} />}
              title={stepName(step)}
              summary={stepSummary(step)}
              index={index}
              isFirst={index === 0}
              isLast={index === steps.length - 1}
              selected={panel?.id === step.id}
              hasError={stepHasError(index)}
              onOpen={() => openStep(step)}
              onMove={(delta) => moveStep(index, delta)}
              onDelete={() => setStepToDelete(index)}
            />
            <AddStepButton
              emailOnly={isIndividual}
              onAdd={(type) => addStep(index + 1, type)}
            />
          </React.Fragment>
        );
      })}
      {errors.steps ? (
        <ErrorText sx={{ textAlign: "center", mt: 0 }}>
          {errors.steps}
        </ErrorText>
      ) : null}

      <EndMarker />

      {/* Sticky save bar */}
      <Box
        sx={{
          position: "sticky",
          bottom: 12,
          zIndex: 3,
          mt: 2,
          px: { xs: 1.5, sm: 2 },
          py: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          flexWrap: "wrap",
          gap: 1,
          bgcolor: "rgba(255, 255, 255, 0.97)",
          border: `1px solid ${BORDER}`,
          borderRadius: "12px",
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
        }}
      >
        <button
          type="button"
          className="small-contained-outlined-button"
          onClick={() => handleSave({ asDraft: true })}
          disabled={saving}
          style={{ opacity: saving ? 0.6 : 1 }}
        >
          Save as draft
        </button>
        {onCancel ? (
          <button
            type="button"
            className="small-contained-outlined-button"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </button>
        ) : null}
        <button
          type="button"
          className="small-contained-button"
          onClick={() => handleSave()}
          disabled={saving}
          style={{ opacity: saving ? 0.6 : 1 }}
        >
          {isNew ? "Save workflow" : "Update"}
        </button>
      </Box>

      {/* AI settings drawer hidden for now
      <AiProviderSettingsDrawer
        open={aiDrawerOpen}
        onClose={() => setAiDrawerOpen(false)}
        onSaved={(setting) => setAiSetting(setting)}
      />
      */}

      {/* Settings of the step picked in the flow (side panel, like the reference builder). */}
      <Drawer
        anchor="right"
        open={Boolean(openStepData)}
        onClose={closePanel}
        // The email editor opens its own dialogs (link, image): let them take focus.
        disableEnforceFocus
        PaperProps={{
          // Wide enough for the email editor and its toolbar.
          sx: { width: { xs: "100%", sm: "min(1000px, 92vw)" }, display: "flex", flexDirection: "column" },
        }}
      >
        {openStepData ? (
          <>
            <Box sx={{ px: 2.5, pt: 1.5, pb: 2, borderBottom: `1px solid ${BORDER}` }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.5 }}>
                <Tooltip title="Previous step">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Previous step"
                      disabled={openIndex <= 0}
                      onClick={() => openStep(steps[openIndex - 1])}
                    >
                      <ExpandLessRoundedIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Next step">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Next step"
                      disabled={openIndex >= steps.length - 1}
                      onClick={() => openStep(steps[openIndex + 1])}
                    >
                      <ExpandMoreRoundedIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Typography sx={{ fontSize: 12, color: MUTED, ml: 0.5 }}>
                  Step {openIndex + 1} of {steps.length}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <IconButton size="small" aria-label="Close" onClick={closePanel}>
                  <CloseRoundedIcon />
                </IconButton>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                    bgcolor: PRIMARY_SOFT,
                    color: PRIMARY,
                  }}
                >
                  <OpenStepIcon sx={{ fontSize: 22 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 16, color: INK }}>
                    {stepTitle(openStepData)}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: MUTED }}>
                    {stepDescription(openStepData)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2 }}>
              <FieldLabel htmlFor="workflow-step-name">Action name</FieldLabel>
              <TextField
                id="workflow-step-name"
                fullWidth
                size="small"
                value={openStepData.name || ""}
                placeholder={stepTitle(openStepData)}
                onChange={(e) => updateStep(openIndex, { name: e.target.value })}
                sx={inputSx}
              />
              <Box sx={{ mt: 2 }}>{renderStepBody(openStepData, openIndex)}</Box>
            </Box>

            <Box
              sx={{
                px: 2.5,
                py: 1.5,
                borderTop: `1px solid ${BORDER}`,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <button
                type="button"
                className="small-contained-outlined-button"
                onClick={() => setStepToDelete(openIndex)}
                style={{ color: "#dc2626", borderColor: "#fca5a5" }}
              >
                Delete
              </button>
              <Box sx={{ flex: 1 }} />
              <button
                type="button"
                className="small-contained-outlined-button"
                onClick={cancelPanel}
              >
                Cancel
              </button>
              <button type="button" className="small-contained-button" onClick={savePanel}>
                Save action
              </button>
            </Box>
          </>
        ) : null}
      </Drawer>

      <CustomConfirmation
        openDelete={stepToDelete !== null}
        setOpenDelete={(open) => {
          if (!open) setStepToDelete(null);
        }}
        title="Delete this step?"
        handleAgree={() => {
          removeStep(stepToDelete);
          setStepToDelete(null);
        }}
        open={false}
        setOpen={() => {}}
      />
    </Box>
  );
}
